import { Injectable } from '@angular/core';
import {
  InventoryService,
  ViwPhotoUploadResponseData,
  photosFormErrorMsg
} from 'remarketing-angular-library';
import {
  DataUrl,
  NgxImageCompressService,
  UploadResponse
} from 'ngx-image-compress';
import { Subject } from 'rxjs';
import { DatePipe } from '@angular/common';

export interface CustomValidatorConfig {
  validatorName?: string,
  validatorValue?: any,
  validatorErrMsgName?: string,
  validatorErrMsgValue?: string
}

@Injectable()
export class PhotoUploadService {
  vehicleVINNumber: string;
  authToken: any;
  errMsg: string;
  addedImageDetails: UploadResponse[];
  currIndex: number;
  public photoUploadResponseData = new Subject();
  public photoUploadErrStatus = new Subject();
  INVENTORY_ITEM_SERVICE_BASE = '/api/inventory-item/v1';

  //Configs for photos form validation
  photosFormValidator = {
    imageCompressorMaxWidth: 1024,
    imageCompressorMaxHeight: 768,
    validByteCount: 20971520,
    validImageCount: 50,
    validImageFormatList: ['image/jpg', 'image/png', 'image/jpeg', 'image/gif']
  };
  constructor(
    private readonly datePipe: DatePipe,
    private readonly imageCompressService: NgxImageCompressService,
    private readonly inventoryService: InventoryService
  ) { }

  /**
   *
   * @param vinNumber - current vehicle VIN number
   * @param customValidator - if user wants to customize the validator and its msg specific to component they use this option
   * Selects the photos from explorer
   */
  selectPhotos(vinNumber, customValidator?: CustomValidatorConfig, currIndex?) {
    this.vehicleVINNumber = vinNumber;
    this.checkForCustomValidator(customValidator);
    this.currIndex = currIndex;
    this.imageCompressService
      .uploadMultipleFiles().then((multipleOrientedFiles: UploadResponse[]) => {
        this.addedImageDetails = [...multipleOrientedFiles];
        this.addSpinnerInPhotosCard();
        if (this.checkImageCountAndFormatIsValid(this.addedImageDetails)) {
          this.authorizeAndCompressImage();
        }
      });
  }

  /**
   *
   * @param customValidator - validator and its msg
   * customize the validator specific to component
   */
  checkForCustomValidator(customValidator: CustomValidatorConfig) {
    if (customValidator) {
      if (customValidator.validatorName) {
        this.photosFormValidator[customValidator.validatorName] = customValidator.validatorValue;
      }
      if (customValidator.validatorErrMsgName) {
        photosFormErrorMsg[customValidator.validatorErrMsgName] = customValidator.validatorErrMsgValue;
      }
    }
  }

  //gets authtoken if it is not there
  authorizeAndCompressImage() {
    if (this.authToken) {
      this.compressImage();
    } else {
      // getVIWAuthToken is an async operation, so we have to call compressImage only after we gets the response
      this.inventoryService.getVIWAuthToken().subscribe((token: any) => {
        this.authToken = token;
        this.compressImage();
      });
    }
  }

  /**
   *
   * @param imageDetails - image response with its properties
   * @returns - whether image count and format is valid or not
   */
  checkImageCountAndFormatIsValid(imageDetails: UploadResponse[]) {
    const isValidCount = imageDetails && (imageDetails.length <= this.photosFormValidator.validImageCount);
    const isInValidFormat = isValidCount && imageDetails.some((item) => {
      const mimeFormat = item.image && item.image.split(',')[0].match(/:(.*?);/)[1];
      return !this.photosFormValidator.validImageFormatList.includes(mimeFormat.toLowerCase());
    });
    if (!isValidCount) {
      this.errMsg = photosFormErrorMsg.PHOTO_COUNT_ERROR;
    } else if (isInValidFormat) {
      this.errMsg = photosFormErrorMsg.PHOTO_FORMAT_ERROR;
    } else {
      this.errMsg = '';
    }
    this.photoUploadErrStatus.next({ errMsg: this.errMsg, currIndex: this.currIndex });
    return isValidCount && !isInValidFormat;
  }

  // it will Compress the image and convert it to dataURL format
  compressImage() {
    this.addedImageDetails.forEach((item) => {
      const fileName = item.fileName;
      this.imageCompressService
        .compressFile(item.image, item.orientation, 100, 80,
          this.photosFormValidator.imageCompressorMaxWidth, this.photosFormValidator.imageCompressorMaxHeight)
        .then((result: DataUrl) => {
          const convertedFile = this.base64toFile(result, fileName);
          //check for valid byte count
          if (convertedFile.size <= this.photosFormValidator.validByteCount) {
            this.sendPhotosToVIW(convertedFile);
            this.errMsg = '';
          } else {
            this.errMsg = photosFormErrorMsg.PHOTO_SIZE_ERROR;
          }
          this.photoUploadErrStatus.next({ errMsg: this.errMsg, currIndex: this.currIndex });
        });
    });
  }

  //Adds spinner for photo cards
  addSpinnerInPhotosCard() {
    const spinnerList = this.addedImageDetails.map((item) => {
      return { showSpinner: true };
    });
    //shows spinner card until VIW response
    this.photoUploadResponseData.next({
      response: { showSpinner: true, spinnerList },
      currIndex: this.currIndex
    });
  }

  /**
   *
   * @param convertedFile - connverted file object from dataURL(base64)
   * construct the formData to send it as payload to VIW
   */
  sendPhotosToVIW(convertedFile) {
    const currentDate = this.datePipe.transform((new Date), 'MM/dd/yyyy');
    const payloadObj = new FormData();
    payloadObj.append('files', convertedFile);
    payloadObj.append('vin', this.vehicleVINNumber);
    payloadObj.append('requestDate', currentDate);
    payloadObj.append('viwToken', `${this.authToken.token_type} ${this.authToken.access_token}`);
    this.inventoryService.uploadPhotoToVIW(payloadObj, this.authToken).subscribe((response: ViwPhotoUploadResponseData) => {
      this.photoUploadResponseData.next({ response, currIndex: this.currIndex });
    });
  }

  //VIW will not accept base64 format so converting it to file object
  base64toFile(base64String, fileName) {
    //separate out the mime component
    const mimeFormat = base64String.split(',')[0].match(/:(.*?);/)[1];

    //atob is an pre-defined javascript function which will decode the encoded base64 string for file conversion
    const byteString = atob(base64String.split(',')[1]);

    // write the bytes of the string to an ArrayBuffer
    const arrayBuffer = new ArrayBuffer(byteString.length);

    // create a view into the buffer
    const int8Array = new Uint8Array(arrayBuffer);

    // set the bytes of the buffer to the correct values
    for (let i = 0; i < byteString.length; i++) {
      int8Array[i] = byteString.charCodeAt(i);
    }
    // write the ArrayBuffer to a blob
    const blob = new Blob([int8Array], { type: mimeFormat });
    return new File([blob], fileName, { type: mimeFormat });
  }
}
